// @story HIR-HD2
// HR's pass / fail / clear for one candidate at one stage (server:
// services/hrRuling.service.js). A small link opens a reason box; the
// server records who and when from the sign-in, freezes the mark and total
// into the record, and never deletes an earlier decision — Clear adds one.
// Pass excuses a low or missing mark but never makes anyone recommended by
// itself. Not offered while the candidate waits in the exit filter (the
// conductor decides that). Nothing here messages the candidate.
import React, { useState } from 'react';
import api from '../../services/api';
import { Button } from '../../ds';

const REASON_MIN = 12;
const small = { fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' };
const linkButton = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 'var(--fs-caption)', color: 'var(--brand-primary-ink)' };

/* The pass or fail in force: the latest entry that is not a clear and has
   not been cleared (the same rule as the server's). */
export function inForce(rulings) {
  const list = Array.isArray(rulings) ? rulings : [];
  const cleared = new Set(list.filter((r) => r.decision === 'clear').map((r) => r.clears));
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i].decision !== 'clear' && !cleared.has(list[i].seq)) return list[i];
  }
  return null;
}

export default function HrStageDecision({ resumeId, stageId, stageResult, onChanged }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (stageResult?.status === 'exit_filter') return null;
  const active = inForce(stageResult?.hrRulings);

  const send = async (decision) => {
    if (reason.trim().length < REASON_MIN) { setErr(`Give a reason of at least ${REASON_MIN} characters.`); return; }
    setBusy(true); setErr('');
    try {
      await api.post(`/candidate-scoring/candidates/${resumeId}/stages/${stageId}/rulings`, { decision, reason: reason.trim() });
      setOpen(false); setReason('');
      if (onChanged) onChanged();
    } catch (e) {
      setErr(e.response?.data?.message || 'Could not record that just now.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 4, justifyItems: 'start', whiteSpace: 'normal' }}>
      {active && <span style={small}>HR {active.decision} · {active.by?.name || 'HR'}</span>}
      {!open ? (
        <button type="button" style={linkButton} onClick={() => setOpen(true)}>
          {active ? 'Clear HR decision' : 'HR pass / fail'}
        </button>
      ) : (
        <div style={{ display: 'grid', gap: 4, minWidth: 220 }}>
          <textarea aria-label="Reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder={`Reason (at least ${REASON_MIN} characters)`}
            style={{ width: '100%', border: '1px solid var(--border, #ddd)', borderRadius: 6, padding: '4px 6px', font: 'inherit', fontSize: 'var(--fs-caption)' }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {active ? (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => send('clear')}>Clear</Button>
            ) : (
              <>
                <Button size="sm" disabled={busy} onClick={() => send('pass')}>Pass</Button>
                <Button size="sm" variant="secondary" disabled={busy} onClick={() => send('fail')}>Fail</Button>
              </>
            )}
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setOpen(false); setErr(''); }}>Cancel</Button>
          </div>
          {err && <span role="alert" style={{ ...small, color: 'var(--status-danger-text)' }}>{err}</span>}
        </div>
      )}
    </div>
  );
}
